/**
 * Service for syncing pending POS transactions to the server
 */

import {
  getPendingSyncTransactions,
  updateTransactionSyncStatus,
  updateCompletedOrderSyncStatus,
  PendingTransaction,
} from './posIndexedDBUtils';

/**
 * Sync a single pending transaction to the server
 * Returns early if transaction is already synced to prevent duplicate sales
 */
export async function syncPendingTransaction(
  transaction: PendingTransaction,
  salesService: {
    createSales: (
      data: PendingTransaction['requestData'],
    ) => Promise<{ id: string }>;
  },
): Promise<{ success: boolean; serverId?: string; error?: string }> {
  // Check if transaction is already synced - prevent duplicate sales
  if (transaction.syncStatus === 'synced' && transaction.serverId) {
    return {
      success: true,
      serverId: transaction.serverId,
    };
  }

  try {
    const sale = await salesService.createSales(transaction.requestData);
    // Update transaction as synced
    await updateTransactionSyncStatus(transaction.id, 'synced', sale.id);
    return { success: true, serverId: sale.id };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to sync transaction';
    // Update transaction with error but keep as pending
    await updateTransactionSyncStatus(
      transaction.id,
      'pending_sync',
      undefined,
      errorMessage,
    );
    return { success: false, error: errorMessage };
  }
}

/**
 * Sync all pending transactions to the server for a specific company
 * Runs in the background and doesn't throw errors
 */
export async function syncAllPendingTransactions(
  companyId: string,
  salesService: {
    createSales: (
      data: PendingTransaction['requestData'],
    ) => Promise<{ id: string }>;
  },
): Promise<void> {
  try {
    const pendingTransactions = await getPendingSyncTransactions(companyId);

    // Sync transactions sequentially to avoid overwhelming the server
    for (const transaction of pendingTransactions) {
      try {
        const result = await syncPendingTransaction(transaction, salesService);
        if (result.success && result.serverId) {
          // Update corresponding completed order if it exists
          // This ensures completed orders show the correct sync status
          try {
            await updateCompletedOrderSyncStatus(
              transaction.id,
              'synced',
              result.serverId,
            );
          } catch {
            // Completed order might not exist yet, which is fine
            // This can happen if sync happens before the Success page stores it
            // The Success page will update it when it stores the completed order
            console.debug(
              'Completed order not found for transaction:',
              transaction.id,
            );
          }
        }
      } catch (error) {
        // Continue with next transaction even if one fails
        console.warn('Failed to sync transaction:', transaction.id, error);
      }
    }
  } catch (error) {
    console.warn('Failed to get pending transactions for sync:', error);
  }
}

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}
